import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BTN, EventChips, FailingAlert, HealthBadge, LastFire, StatusBadge } from "@/components/postbacks/shared";
import { AdminReviewDialog } from "@/components/postbacks/AdminReview";

const TABS = ["pending", "failing", "approved", "rejected", "disabled", "all"];
const TAB_LABEL = { pending: "Waiting", failing: "Failing", approved: "Live", rejected: "Rejected", disabled: "Turned off", all: "All" };

function Toggle({ label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-black mt-1 h-4 w-4"
        data-testid={`${testId}-input`} />
      <span>
        <span className="text-sm font-semibold">{label}</span>
        <span className="block text-xs text-zinc-600">{hint}</span>
      </span>
    </label>
  );
}

function SettingsCard({ settings, onSaved }) {
  const save = async (patch) => {
    try {
      const { data } = await api.put("/admin/postbacks/settings", { ...settings, ...patch });
      onSaved(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    }
  };
  return (
    <div className="brut-card p-4 sm:p-5 grid gap-4 md:grid-cols-2" data-testid="postback-settings">
      <Toggle testId="setting-require-approval" checked={settings.require_approval} onChange={(v) => save({ require_approval: v })}
        label="New postbacks need my approval"
        hint="Off: affiliates' and media buyers' postback links go live as soon as they save them. Ad platform connections follow the same rule." />
      <Toggle testId="setting-affiliate-payout" checked={settings.affiliate_payout} onChange={(v) => save({ affiliate_payout: v })}
        label="Send affiliates their payout"
        hint="On: affiliates can add {payout}, their own price for that lead's state. Media buyers never get money amounts, and buyer prices are never sent." />
    </div>
  );
}

function Row({ r, onOpen }) {
  return (
    <tr className="border-b border-zinc-200 align-top" data-testid={`admin-postback-row-${r.id}`}>
      <td className="py-2 px-3">
        <div className="font-semibold">{r.owner_name}</div>
        <div className="label-mono">{r.owner_type === "affiliate" ? "Affiliate" : "Media buyer"}</div>
      </td>
      <td className="py-2 px-3">
        <div className="font-semibold">{r.name}</div>
        <div className="label-mono">{r.kind === "pixel" ? "Browser pixel (retired)" : r.kind === "connection" ? "Ad platform connection" : "Postback"}</div>
      </td>
      <td className="py-2 px-3 max-w-[420px]">
        {r.kind === "pixel"
          ? <span className="text-xs">{r.where === "thank_you" ? "Thank-you page" : "Landing pages"}{r.scan_verdict === "blocked" && <span className="text-red-700"> · safety block</span>}</span>
          : <><div className="font-mono text-[11px] break-all">{r.kind === "connection" ? `${r.platform} · ${r.pixel_id}` : r.url}</div><div className="mt-1"><EventChips events={r.events} /></div></>}
      </td>
      <td className="py-2 px-3">
        <div className="flex flex-col items-start gap-1">
          <StatusBadge row={r} testId={`admin-postback-${r.id}-status`} />
          <HealthBadge row={r} testId={`admin-postback-${r.id}-health`} />
        </div>
      </td>
      <td className="py-2 px-3">{r.kind !== "pixel" && <LastFire fire={r.last_fire} testId={`admin-postback-${r.id}-last`} />}</td>
      <td className="py-2 px-3">
        <button type="button" onClick={() => onOpen(r.id)} className={BTN} data-testid={`admin-postback-${r.id}-review`}>Review</button>
      </td>
    </tr>
  );
}

export default function AdminPostbacks() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [failing, setFailing] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    const [{ data: d }, { data: f }] = await Promise.all([
      api.get(`/admin/postbacks?status=${tab}`), api.get("/admin/postbacks?status=failing"),
    ]);
    setData(d);
    setFailing(f.items);
  }, [tab]);
  useEffect(() => {
    load().catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load postbacks"));
  }, [load]);
  return (
    <div className="space-y-6" data-testid="admin-postbacks-page">
      <div>
        <div className="label-mono">Tracking</div>
        <h1 className="font-chivo font-black text-4xl sm:text-5xl tracking-tighter uppercase">Postback Approvals</h1>
        <p className="text-sm text-zinc-700 mt-2 max-w-3xl">
          Postback links and ad platform connections that affiliates and media buyers set up. Both run from our server, never on our pages. Approve them before they go live, or turn any off.
        </p>
      </div>
      {data && <SettingsCard settings={data.settings} onSaved={(s) => setData({ ...data, settings: s })} />}
      <FailingAlert rows={failing} showOwner testId="admin-postbacks-failing-alert">
        {tab !== "failing" && (
          <button type="button" onClick={() => setTab("failing")} className={`${BTN} mt-2 border-red-700 text-red-800`}
            data-testid="admin-postbacks-failing-open">Show failing postbacks</button>
        )}
      </FailingAlert>
      <div className="flex flex-wrap gap-1.5" data-testid="admin-postback-tabs">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} aria-pressed={tab === t} data-testid={`admin-postback-tab-${t}`}
            className={`border border-black px-2.5 py-1 text-[10px] uppercase tracking-widest ${tab === t ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
            {TAB_LABEL[t]}{t !== "all" && data ? ` (${data.counts[t] || 0})` : ""}
          </button>
        ))}
      </div>
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="admin-postbacks-table">
          <thead>
            <tr className="border-b border-black label-mono text-left">
              <th className="py-2 px-3">From</th><th className="py-2 px-3">Name</th><th className="py-2 px-3">What</th>
              <th className="py-2 px-3">Status</th><th className="py-2 px-3">Last sent</th><th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((r) => <Row key={r.id} r={r} onOpen={setOpenId} />)}
            {data && !data.items.length && (
              <tr><td colSpan={6} className="py-6 text-center label-mono" data-testid="admin-postbacks-empty">Nothing here</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {openId && <AdminReviewDialog id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
